import { useState } from "react";
import { Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  Users, CurrencyGbp, Globe, ChartLine, SignOut, ShieldCheck, House, Lock, Ticket, Flag, Database, FloppyDisk,
} from "@phosphor-icons/react";

const NAV = [
  { path: "/admin", label: "Users", icon: Users, end: true },
  { path: "/admin/pricing", label: "Pricing", icon: CurrencyGbp },
  { path: "/admin/coupons", label: "Coupons", icon: Ticket },
  { path: "/admin/submissions", label: "Submissions", icon: Flag },
  { path: "/admin/course-data", label: "Course data", icon: Database },
  { path: "/admin/backups", label: "Backups", icon: FloppyDisk },
  { path: "/admin/domain", label: "Domain", icon: Globe },
  { path: "/admin/revenue", label: "Revenue", icon: ChartLine },
];

/**
 * cPanel-style chrome for the admin dashboard.
 * Lives at top level (/admin/*), NOT inside the mobile AppShell.
 * Has its own sidebar, dark topbar, and wide layout.
 */
export default function AdminLayout({ children }) {
  const { user, loading, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0c2a20" }}>
        <div className="text-[#F2EEE7] font-heading text-lg">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  // Logged in as a non-admin — send them to the admin login screen with a
  // hint message instead of silently bouncing to /app (which was confusing:
  // a user would think the admin dashboard was broken when really they were
  // just signed in under the wrong account).
  if (user.role !== "admin") return <Navigate to="/admin/login?reason=not-admin" replace />;

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" style={{ background: "#0c2a20", color: "#F2EEE7" }}>
      {/* Sidebar (desktop) */}
      <aside
        className="hidden lg:flex w-64 shrink-0 flex-col"
        style={{ background: "#143D2F", borderRight: "1px solid rgba(242,238,231,0.06)" }}
      >
        <div className="p-5 border-b border-white/5">
          <div className="flex items-center gap-2">
            <img src="/fairway-logo-transparent.png" alt="Fairway" className="h-9 w-auto" />
            <div className="text-[10px] uppercase tracking-[3px] opacity-60 font-bold ml-1 text-[#C4543C]">Control room</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1" data-testid="admin-sidebar">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = item.end ? loc.pathname === item.path : loc.pathname.startsWith(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold font-heading transition-colors ${
                  active ? "bg-[#C4543C] text-[#F2EEE7]" : "text-[#F2EEE7]/70 hover:bg-white/5 hover:text-[#F2EEE7]"
                }`}
              >
                <Icon size={18} weight={active ? "fill" : "regular"} />
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 space-y-1 border-t border-white/5">
          <Link
            to="/app"
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[#F2EEE7]/70 hover:bg-white/5 hover:text-[#F2EEE7]"
            data-testid="admin-back-to-app"
          >
            <House size={18} /> Back to app
          </Link>
          <button
            onClick={() => { logout(); nav("/admin/login"); }}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-[#F2EEE7]/70 hover:bg-white/5 hover:text-[#F2EEE7] w-full text-left"
            data-testid="admin-logout"
          >
            <SignOut size={18} /> Sign out
          </button>
        </div>
        <div className="p-4 text-[10px] text-white/40 uppercase tracking-wider">
          Signed in as <span className="text-white/70 normal-case tracking-normal font-bold">{user.email}</span>
        </div>
      </aside>

      {/* Mobile topbar */}
      <header
        className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-white/5"
        style={{ background: "#143D2F" }}
      >
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} weight="fill" color="#C4543C" />
          <div className="font-heading font-bold text-sm">Fairway · Admin</div>
        </div>
        <button
          onClick={() => setMobileNavOpen((v) => !v)}
          className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg"
          style={{ background: "rgba(255,255,255,0.08)" }}
          data-testid="admin-mobile-nav-toggle"
        >
          {mobileNavOpen ? "Close" : "Menu"}
        </button>
      </header>

      {/* Mobile sliding nav */}
      {mobileNavOpen && (
        <div className="lg:hidden px-3 py-2 border-b border-white/5" style={{ background: "#143D2F" }}>
          <div className="grid grid-cols-2 gap-2" data-testid="admin-mobile-nav">
            {NAV.map((item) => {
              const Icon = item.icon;
              const active = item.end ? loc.pathname === item.path : loc.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileNavOpen(false)}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-bold font-heading ${
                    active ? "bg-[#C4543C]" : "bg-white/5"
                  }`}
                >
                  <Icon size={16} weight={active ? "fill" : "regular"} /> {item.label}
                </Link>
              );
            })}
            <Link to="/app" className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm bg-white/5 font-heading font-bold">
              <House size={16} /> Back to app
            </Link>
            <button
              onClick={() => { logout(); nav("/admin/login"); }}
              className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm bg-white/5 font-heading font-bold text-left"
            >
              <SignOut size={16} /> Sign out
            </button>
          </div>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0" style={{ background: "#F2EEE7", color: "#143D2F" }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6 sm:py-10">{children}</div>
      </main>
    </div>
  );
}

/* Dedicated admin login (separate from user login so admins land on the dashboard) */
export function AdminLogin() {
  const { user, login, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // ?reason=not-admin is set by AdminLayout when a non-admin user lands here.
  const reason = new URLSearchParams(loc.search).get("reason");

  // If they're already signed in AS the admin, send them straight to the dashboard.
  // If they're signed in as a regular user, DON'T silently bounce — show this
  // screen with a hint and a "sign out" button (so they can switch accounts).
  if (user && user.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), pw);
      nav("/admin", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Sign in failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ background: "#0c2a20" }}>
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 mb-6">
          <img src="/fairway-logo-transparent.png" alt="Fairway" className="h-11 w-auto" />
          <div className="text-[10px] uppercase tracking-[3px] opacity-60 font-bold ml-1 text-[#C4543C]">Control room</div>
        </div>
        <h1 className="text-[#F2EEE7] font-heading font-black text-3xl mb-2">Sign in</h1>
        <p className="text-[#F2EEE7]/60 text-sm mb-6">Admin access only.</p>

        {/* Hint shown when a non-admin user tried to hit /admin and got bounced here */}
        {(reason === "not-admin" || (user && user.role !== "admin")) && (
          <div
            className="text-sm rounded-xl px-3 py-3 border mb-4"
            style={{ background: "rgba(196,84,60,0.12)", borderColor: "#C4543C", color: "#F2EEE7" }}
            data-testid="admin-login-not-admin-hint"
          >
            <div className="font-bold mb-1">You&apos;re signed in as a regular user{user?.email ? ` (${user.email})` : ""}.</div>
            <div className="text-xs opacity-80 mb-2">
              Sign out, then sign back in with your admin account to reach the dashboard.
            </div>
            <button
              type="button"
              onClick={async () => { await logout(); }}
              className="text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg"
              style={{ background: "#F2EEE7", color: "#0c2a20" }}
              data-testid="admin-login-signout"
            >
              Sign out
            </button>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-3" data-testid="admin-login-form">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#F2EEE7]/60 block mb-1.5">Email</label>
            <input
              type="email" required autoFocus
              value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-login-email"
              className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-[#F2EEE7] focus:outline-none focus:border-[#C4543C]"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#F2EEE7]/60 block mb-1.5">Password</label>
            <input
              type="password" required
              value={pw} onChange={(e) => setPw(e.target.value)}
              data-testid="admin-login-password"
              className="w-full px-4 py-3 rounded-xl border border-white/10 bg-white/5 text-[#F2EEE7] focus:outline-none focus:border-[#C4543C]"
            />
          </div>
          {error && (
            <div className="text-sm rounded-xl px-3 py-2 border" style={{ background: "rgba(196,84,60,0.1)", borderColor: "#C4543C", color: "#F2EEE7" }} data-testid="admin-login-error">
              {error}
            </div>
          )}
          <button
            type="submit" disabled={submitting}
            className="w-full px-4 py-3 rounded-xl font-heading font-bold disabled:opacity-60"
            style={{ background: "#C4543C", color: "#F2EEE7" }}
            data-testid="admin-login-submit"
          >
            <span className="flex items-center justify-center gap-2">
              <Lock size={16} weight="bold" />
              {submitting ? "Signing in..." : "Sign in"}
            </span>
          </button>
        </form>
        <p className="text-center text-xs text-[#F2EEE7]/40 mt-6">
          <Link to="/forgot-password" className="hover:text-[#F2EEE7]/80">Forgot password?</Link>
          {" · "}
          <Link to="/" className="hover:text-[#F2EEE7]/80">Back to Fairway</Link>
        </p>
      </div>
    </div>
  );
}
